import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/api/axiosInstance.js';

export const useLiveAvailability = (packageId) => {
  const [availability, setAvailability] = useState({
    available: 0,
    total: 0,
    held: 0,
    sold: 0,
    updatedAt: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);

  const fetchInitialAvailability = useCallback(async () => {
    if (!packageId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/packages/${packageId}/availability`);
      setAvailability({
        available: response.data.available,
        total: response.data.total,
        held: response.data.held,
        sold: response.data.sold,
        updatedAt: response.data.updatedAt
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch availability');
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    if (!packageId) {
      setLoading(false);
      return;
    }

    fetchInitialAvailability();

    const baseURL = axiosInstance.defaults.baseURL;
    const es = new EventSource(`${baseURL}/packages/${packageId}/stream`);
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Compare as strings since req.params gives string, DB gives number
        if (String(data.packageId) === String(packageId)) {
          setAvailability({
            available: data.available,
            total: data.total,
            held: data.held,
            sold: data.sold,
            updatedAt: data.updatedAt
          });
        }
      } catch (_) {}
    };

    es.onerror = () => {
      setIsConnected(false);
      // Close immediately on error — do not let EventSource auto-reconnect
      // and spam state updates. The initial fetch already has the data.
      es.close();
      eventSourceRef.current = null;
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [packageId, fetchInitialAvailability]);

  const refresh = useCallback(() => {
    fetchInitialAvailability();
  }, [fetchInitialAvailability]);

  return {
    availability,
    loading,
    error,
    isConnected,
    refresh
  };
};
